import { isLiabilityType } from '../../shared/account-types.js';
import { roundAmount } from '../../shared-server-web/round-amount.js';
import { getCurrencyRateMap, getCurrencyRateSnapshot } from '../currency-rates.js';
import { convertBetweenWithMap } from '../currency-rates-helpers.js';

/** @type {ReadonlyArray<string>} */
export const AMOUNT_OPERATORS = Object.freeze(['eq', 'gte', 'lte', 'between']);

/**
 * Strip grouping separators / currency symbols for numeric parse.
 * Keeps digits, minus, dot.
 * @param {string} raw
 * @returns {string}
 */
export function stripAmountInput(raw) {
	return String(raw ?? '').replace(/[,_\s\u00A0]/g, '').trim();
}

/**
 * Validate amount string is finite number (with optional minus/dot).
 * @param {string} s
 * @returns {boolean}
 */
function isValidAmountStr(s) {
	if (!s || s === '-' || s === '.' || s === '-.') { return false; }
	return /^-?\d+(\.\d+)?$/.test(s) && Number.isFinite(Number(s));
}

/**
 * @param {string} value
 * @returns {{ op: string, amount: string | null, amountEnd: string | null, currency: string | null }}
 */
export function parseAmountClause(value) {
	if (!value) { return { op: '', amount: null, amountEnd: null, currency: null }; }
	const parts = String(value).split(':');
	const op = parts[0] ?? '';
	if (op === 'eq' || op === 'gte' || op === 'lte') {
		if (parts.length !== 3) { return { op: '', amount: null, amountEnd: null, currency: null }; }
		return { op, amount: parts[1] ?? null, amountEnd: null, currency: parts[2] ?? null };
	}
	if (op === 'between') {
		if (parts.length !== 4) { return { op: '', amount: null, amountEnd: null, currency: null }; }
		return { op, amount: parts[1] ?? null, amountEnd: parts[2] ?? null, currency: parts[3] ?? null };
	}
	return { op: '', amount: null, amountEnd: null, currency: null };
}

/**
 * @param {string} op
 * @param {string} amount
 * @param {string} currency
 * @param {string | null} [amountEnd]
 * @returns {string}
 */
export function buildAmountClause(op, amount, currency, amountEnd) {
	if (op === 'between') { return `between:${amount}:${amountEnd ?? ''}:${currency ?? ''}`; }
	return `${op}:${amount}:${currency ?? ''}`;
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isAmountClause(value) {
	if (!value) { return false; }
	if (/^(eq|gte|lte):-?\d+(\.\d+)?:[A-Z0-9]{2,10}$/.test(value)) { return true; }
	if (/^between:-?\d+(\.\d+)?:-?\d+(\.\d+)?:[A-Z0-9]{2,10}$/.test(value)) { return true; }
	return false;
}

/**
 * Resolve row currency from item.
 * @param {*} item
 * @returns {string | null}
 */
function resolveRowCurrency(item) {
	if (!item || typeof item !== 'object') { return null; }
	if (item.currency) { return String(item.currency); }
	if (item.currencyCode) { return String(item.currencyCode); }
	return null;
}

/**
 * Resolve account type for liability inversion.
 * @param {*} item
 * @returns {string | null}
 */
function resolveAccountType(item) {
	if (!item || typeof item !== 'object') { return null; }
	if (item.accountType) { return String(item.accountType); }
	if (item.account_type) { return String(item.account_type); }
	if (item.accountTypeName) { return String(item.accountTypeName); }
	return null;
}

/**
 * @param {string} op
 * @param {string} rawLow
 * @param {string} rawHigh
 * @param {string | null} ccy
 * @returns {{ low: number, high: number | null } | null}
 */
function parseFilterBounds(op, rawLow, rawHigh, ccy) {
	if (!ccy || !/^[A-Z0-9]{2,10}$/.test(ccy)) { return null; }
	if (!isValidAmountStr(rawLow)) { return null; }
	if (op === 'between' && !isValidAmountStr(rawHigh)) { return null; }
	const low = Number(rawLow);
	const high = op === 'between' ? Number(rawHigh) : null;
	if (high !== null && Number.isNaN(high)) { return null; }
	return { low, high };
}

/**
 * @param {number} hay
 * @param {string} rowCcy
 * @param {string} ccy
 * @param {*} item
 * @returns {number | null}
 */
function convertHaystack(hay, rowCcy, ccy, item) {
	let hayEconomic = hay;
	const acctType = resolveAccountType(item);
	if (acctType && isLiabilityType(acctType)) { hayEconomic = -hay; }
	if (rowCcy === ccy) { return hayEconomic; }
	const map = getCurrencyRateMap();
	const converted = convertBetweenWithMap(hayEconomic, rowCcy, ccy, map);
	if (converted === null || converted === undefined || !Number.isFinite(converted)) { return null; }
	void getCurrencyRateSnapshot();
	return converted;
}

/**
 * @param {number} converted
 * @param {string} op
 * @param {number} low
 * @param {number | null} high
 * @returns {boolean}
 */
function compareAmount(converted, op, low, high) {
	const c = roundAmount(converted, 2);
	const l = roundAmount(low, 2);
	if (op === 'eq') { return c === l; }
	if (op === 'gte') { return c >= l; }
	if (op === 'lte') { return c <= l; }
	if (op === 'between' && high !== null) {
		const h = roundAmount(high, 2);
		const lo = Math.min(l, h);
		const hi = Math.max(l, h);
		return c >= lo && c <= hi;
	}
	return false;
}

/**
 * Check if haystack amount matches clause in filter currency.
 * Converts haystack (row amount) into filter currency before comparison.
 * Liability types invert economic sign: -value.
 * Equality uses 2dp rounded comparison.
 *
 * @param {number | string | null | undefined} haystackAmount
 * @param {string} clauseValue
 * @param {*} item
 * @returns {boolean}
 */
export function matchesAmountClause(haystackAmount, clauseValue, item) {
	const parsed = parseAmountClause(clauseValue);
	const op = parsed.op;
	if (!op || !AMOUNT_OPERATORS.includes(op)) { return false; }
	const rawLow = stripAmountInput(parsed.amount ?? '');
	const rawHigh = stripAmountInput(parsed.amountEnd ?? '');
	const ccy = parsed.currency ? String(parsed.currency).trim().toUpperCase() : null;
	const bounds = parseFilterBounds(op, rawLow, rawHigh, ccy);
	if (!bounds) { return false; }
	const hay = Number(haystackAmount);
	if (!Number.isFinite(hay)) { return false; }
	if (hay === Number.NEGATIVE_INFINITY || hay === Number.POSITIVE_INFINITY) { return false; }
	const rowCcy = resolveRowCurrency(item);
	if (!rowCcy || !ccy) { return false; }
	const converted = convertHaystack(hay, rowCcy, ccy, item);
	if (converted === null) { return false; }
	return compareAmount(converted, op, bounds.low, bounds.high);
}
